/**
 * UAVTradeZone Modification Record
 *
 * Change ID:    TOAST_MSG_UPDATE_IRIT_2026
 * Date:         2026-01-27
 * Modified By:  Cursor
 *
 * Description:
 * Updated Toastify messages to Irit's improved user-friendly versions.
 *
 * Related Files: utils/notifications.ts
 */

/**
 * UAVTradeZone Modification Record
 *
 * Change ID:    FIX_LOCATION_FIELD_COLORS
 * Date:         2025-12-29
 * Modified By:  Cursor
 *
 * Description:
 * Add getDbAttr() to userCity, userState, userCountry fields to ensure
 * loaded data appears in BLACK color (not grey placeholder color).
 * The data-db attribute triggers CSS rule: .field-input[data-db="true"] { color: black; }
 *
 * File Size Check:
 * - Current: 237 lines
 * - Adding: ~15 lines (helper + attributes)
 * - Projected: ~252 lines
 * - Decision: Proceed (within 200-line limit excluding headers/comments)
 *
 * Related Screens / Modules:
 * - client/src/components/setup/user-profile/components/PersonalInformation.tsx
 * - client/src/styles/components/_forms.scss (CSS rules)
 */
/**
 * UAVTradeZone Modification Record
 *
 * Change ID:    PERSONAL_INFO_USER_LOCATION_DROPDOWN
 * Date:         2025-12-29
 * Modified By:  Cursor
 *
 * Description:
 * Add userCity, userState, userCountry fields to PersonalInformation component.
 * Country uses DROPDOWN with countries list (same logic as CompanyAddress.tsx).
 * Import countries from utils/countries.ts (195+ countries).
 * 
 * Field Implementation:
 * - userCity: Text input (optional)
 * - userState: Text input (optional)
 * - userCountry: Dropdown with countries list (required)
 * 
 * Field Mapping:
 * - Form fields: userCity, userState, userCountry
 * - Database fields: userCity, userState, userCountry (in user profile section)
 * - Display labels: "City", "State", "Country*" (user-facing)
 *
 * File Size Check:
 * - Current: 147 lines
 * - Adding: ~52 lines (including import)
 * - Projected: ~199 lines
 * - Decision: Proceed (within 200-line limit)
 *
 * Related Screens / Modules:
 * - client/src/components/setup/user-profile/components/PersonalInformation.tsx
 * - client/src/utils/countries.ts (country list)
 * - client/src/components/setup/company-profile/components/CompanyAddress.tsx (reference)
 * - server/models/User.js (userCity, userState, userCountry added)
 */
/**
 * UAVTradeZone Modification Record
 *
 * Change ID:    RENAME_UPLOAD_LABEL
 * Date:         2025-12-29
 * Modified By:  Cursor
 *
 * Description:
 * Renamed "Upload profile picture" to "Profile picture" in the personal information section.
 *
 * Related Screens / Modules:
 * - client/src/components/setup/user-profile/components/PersonalInformation.tsx
 */
/**
 * UAVTradeZone Modification Record
 *
 * Change ID:    SPLIT_USER_PROFILE_PERSONAL
 * Date:         2025-12-29
 * Modified By:  Cursor
 *
 * Description:
 * Extracted personal information section from UserProfileForm.tsx.
 * Fixed incorrect relative import paths and implicit any types.
 *
 * Related Screens / Modules:
 * - client/src/components/setup/user-profile/components/PersonalInformation.tsx
 */
import React, { useRef, useState } from "react";
import { Form, Row, Col, Card, OverlayTrigger, Tooltip } from "react-bootstrap";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faSpinner } from "@fortawesome/free-solid-svg-icons";
import { resizeImage, validateImageFile } from "../../../../utils/imageResizer";
import PhoneInput from "../../../ui/PhoneInput";
import { notify } from "../../../../utils/notifications";
// [2025-12-29] Import countries list for user country dropdown (same as company profile); selectableCountries excludes restricted countries
import { selectableCountries } from "../../../../utils/countries";
// [2026-02-07] Import states utility for country-specific state dropdowns
import { getStatesForCountry, hasStates, getStateLabel } from "../../../../utils/states";
// [2026-02-13] CURRENCY_PHASE1: Currency labels and auto-detect
import { CURRENCIES, CURRENCY_LABELS, getCurrencyForCountry } from "../../../../interfaces/Currency";

interface PersonalInformationProps {
  formik: any;
  dbPresence: Record<string, boolean>;
}

export const PersonalInformation: React.FC<PersonalInformationProps> = ({ formik, dbPresence }) => {
  const fileInputRef = useRef<HTMLInputElement | null>(null);
  const [isProcessingImage, setIsProcessingImage] = useState(false);
  const [isAvatarDragOver, setIsAvatarDragOver] = useState(false);

  // [2025-12-29] Helper to set data-db attribute for CSS color switching (grey -> black)
  const getDbAttr = (fieldName: string) => {
    return dbPresence[fieldName] ? { "data-db": "true" } : {};
  };

  const getImageSrc = (img: any): string => {
    return img.data.startsWith("data:") ? img.data : `data:${img.contentType};base64,${img.data}`;
  };

  const handleAvatarClick = () => {
    if (isProcessingImage) return;
    fileInputRef.current?.click();
  };

  const handleImageUpload = async (files: FileList | null) => {
    if (!files || files.length === 0) return;
    const file = files[0];
    setIsProcessingImage(true);
    try {
      const validationError = validateImageFile(file);
      if (validationError) {
        // [2026-01-27] TOAST_MSG_UPDATE_IRIT_2026: Updated to user-friendly message
        notify.error(null, `We couldn't use this file. ${validationError}`);
        return;
      }
      const resizedImage = await resizeImage(file, 500, 500, 0.8, 500);
      formik.setFieldValue("profileImages", resizedImage);
    } catch (error) {
      console.error("Error processing image:", error);
      // [2026-01-27] TOAST_MSG_UPDATE_IRIT_2026: Updated to user-friendly message
      notify.error(null, "We couldn't process the image. Please try again");
    } finally {
      setIsProcessingImage(false);
    }
  };

  return (
    <div className="mt-3">
      {/* [2025-12-29] RENAME_UPLOAD_LABEL: Rename "Upload profile picture" to "Profile picture" */}
      <div className="field-name mb-2">Profile picture</div>
      <div
        className={`upload-logo-container ${formik.values.profileImages ? "upload-logo-container-has-logo" : ""} ${isProcessingImage ? "upload-logo-container-disabled" : ""} ${isAvatarDragOver ? "upload-logo-container-drag" : ""}`}
        onClick={handleAvatarClick}
        onDragOver={(e: React.DragEvent) => { e.preventDefault(); setIsAvatarDragOver(true); }}
        onDragLeave={() => setIsAvatarDragOver(false)}
        onDrop={(e: React.DragEvent) => { e.preventDefault(); setIsAvatarDragOver(false); handleImageUpload(e.dataTransfer.files); }}
        role="button"
      >
        {formik.values.profileImages ? (
          <div className="upload-logo-preview">
            <img alt="Profile" src={getImageSrc(formik.values.profileImages)} className="upload-logo-image" />
            <button type="button" onClick={(e) => { e.stopPropagation(); formik.setFieldValue("profileImages", null); }} className="upload-logo-remove-btn">
              <svg width="12" height="12" viewBox="0 0 400 512" fill="currentColor"><path d="M55.1 73.4c-12.5-12.5-32.8-12.5-45.3 0s-12.5 32.8 0 45.3L147.2 256 9.9 393.4c-12.5 12.5-12.5 32.8 0 45.3s32.8 12.5 45.3 0L192.5 301.3 329.9 438.6c12.5 12.5 32.8 12.5 45.3 0s12.5-32.8 0-45.3L237.8 256 375.1 118.6c12.5-12.5 12.5-32.8 0-45.3s-32.8-12.5-45.3 0L192.5 210.7 55.1 73.4z" /></svg>
            </button>
          </div>
        ) : (
          <>
            <div className="upload-logo-icon-section">
              <div className="upload-logo-icon-box">
                {isProcessingImage ? <FontAwesomeIcon icon={faSpinner} spin /> : <img src="/assets/upload.svg" alt="Upload" className="upload-icon" />}
              </div>
            </div>
            <div className="upload-logo-text-section">
              {/* [2025-12-29] RENAME_UPLOAD_LABEL: Rename "Upload profile picture" to "Profile picture" */}
              <div className="upload-logo-title">Profile picture</div>
              <div className="upload-logo-subtitle">Drag & Drop or Choose file to upload</div>
            </div>
          </>
        )}
      </div>
      <input ref={fileInputRef} type="file" accept="image/*" className="d-none" onChange={(e) => handleImageUpload(e.currentTarget.files)} disabled={isProcessingImage} />
      
      <Card.Title className="company-profile-title mt-4 mb-3">My information</Card.Title>

      <Row className="align-items-center mb-3">
        <Col md={4}><Form.Label className="field-name mb-0">First name</Form.Label></Col>
        <Col md={8}><Form.Control className="field-input" {...formik.getFieldProps("firstName")} {...getDbAttr("firstName")} isInvalid={!!(formik.touched.firstName && formik.errors.firstName)} /></Col>
      </Row>
      <Row className="align-items-center mb-3">
        <Col md={4}><Form.Label className="field-name mb-0">Last name</Form.Label></Col>
        <Col md={8}><Form.Control className="field-input" {...formik.getFieldProps("lastName")} {...getDbAttr("lastName")} isInvalid={!!(formik.touched.lastName && formik.errors.lastName)} /></Col>
      </Row>
      <Row className="align-items-center mb-3">
        <Col md={4}><Form.Label className="field-name mb-0">Position</Form.Label></Col>
        <Col md={8}><Form.Control className="field-input" {...formik.getFieldProps("jobTitle")} {...getDbAttr("jobTitle")} /></Col>
      </Row>
      <Row className="align-items-center mb-3">
        <Col md={4}><Form.Label className="field-name mb-0">Email</Form.Label></Col>
        <Col md={8}><Form.Control className="field-input" {...formik.getFieldProps("email")} {...getDbAttr("email")} isInvalid={!!(formik.touched.email && formik.errors.email)} /></Col>
      </Row>
      <Row className="align-items-center mb-3">
        <Col md={4}><Form.Label className="field-name mb-0">Tel</Form.Label></Col>
        <Col md={8}>
          {/* [2026-02-06] Pass userCountry to sync phone input country when dropdown changes */}
          <PhoneInput
            name="phone"
            value={formik.values.phone || ""}
            onChange={(val: string) => formik.setFieldValue("phone", val)}
            onBlur={formik.handleBlur}
            isInvalid={!!(formik.touched.phone && formik.errors.phone)}
            country={formik.values.userCountry}
          />
        </Col>
      </Row>

      {/* [2025-12-29] Figma alignment: User personal city (separate from company) */}
      <Row className="align-items-center mb-3">
        <Col md={4}><Form.Label className="field-name mb-0">City</Form.Label></Col>
        <Col md={8}>
          <Form.Control 
            className="field-input" 
            placeholder="Your city"
            {...formik.getFieldProps("userCity")} 
            {...getDbAttr("userCity")} 
            isInvalid={!!(formik.touched.userCity && formik.errors.userCity)} 
          />
          <Form.Control.Feedback type="invalid">
            {formik.touched.userCity && formik.errors.userCity}
          </Form.Control.Feedback>
        </Col>
      </Row>

      {/* [2026-02-07] Country moved before State for better UX - state dropdown depends on country */}
      <Row className="align-items-center mb-3">
        <Col md={4}><Form.Label className="field-name mb-0">Country*</Form.Label></Col>
        <Col md={8}>
          <Form.Select
            className="field-select"
            {...formik.getFieldProps("userCountry")}
            {...getDbAttr("userCountry")}
            isInvalid={!!(formik.touched.userCountry && formik.errors.userCountry)}
            onChange={(e) => {
              formik.handleChange(e);
              // Clear state when country changes if new country has different states
              if (hasStates(e.target.value) !== hasStates(formik.values.userCountry)) {
                formik.setFieldValue("userState", "");
              }
              // [2026-02-13] CURRENCY_PHASE1: Auto-detect currency from country selection
              if (e.target.value) {
                formik.setFieldValue("currency", getCurrencyForCountry(e.target.value));
              }
            }}
          >
            <option value="">Select country</option>
            {selectableCountries.map((c: string) => (
              <option key={c} value={c}>{c}</option>
            ))}
          </Form.Select>
          <Form.Control.Feedback type="invalid">
            {formik.touched.userCountry && formik.errors.userCountry}
          </Form.Control.Feedback>
        </Col>
      </Row>

      {/* [2026-02-07] State field - dropdown for countries with states, text input for others */}
      <Row className="align-items-center mb-3">
        <Col md={4}>
          <Form.Label className="field-name mb-0">
            {formik.values.userCountry ? getStateLabel(formik.values.userCountry) : "State"}
          </Form.Label>
        </Col>
        <Col md={8}>
          {hasStates(formik.values.userCountry) ? (
            <Form.Select
              className="field-select"
              {...formik.getFieldProps("userState")}
              {...getDbAttr("userState")}
              isInvalid={!!(formik.touched.userState && formik.errors.userState)}
            >
              <option value="">Select {getStateLabel(formik.values.userCountry).toLowerCase()}</option>
              {getStatesForCountry(formik.values.userCountry).map((s: string) => (
                <option key={s} value={s}>{s}</option>
              ))}
            </Form.Select>
          ) : (
            // [2026-02-07] Disable state field when country doesn't have predefined states
            <Form.Control
              className="field-input"
              placeholder={formik.values.userCountry ? "Not applicable" : "Select country first"}
              {...formik.getFieldProps("userState")}
              {...getDbAttr("userState")}
              isInvalid={!!(formik.touched.userState && formik.errors.userState)}
              disabled={true}
            />
          )}
          <Form.Control.Feedback type="invalid">
            {formik.touched.userState && formik.errors.userState}
          </Form.Control.Feedback>
        </Col>
      </Row>

      {/* [2026-02-13] CURRENCY_PHASE1: Enhanced currency selector with labels, hover tooltip, validation, auto-detect */}
      <Row className="align-items-center mb-3">
        <Col md={4}>
          <OverlayTrigger placement="top" overlay={<Tooltip>Preferred currency for price display</Tooltip>}>
            <Form.Label className="field-name mb-0" style={{ cursor: "help" }}>Currency</Form.Label>
          </OverlayTrigger>
        </Col>
        <Col md={8}>
          <Form.Select
            className="field-select"
            {...formik.getFieldProps("currency")}
            {...getDbAttr("preferredCurrency")}
            isInvalid={!!(formik.touched.currency && formik.errors.currency)}
          >
            {CURRENCIES.map((c) => (
              <option key={c} value={c}>{CURRENCY_LABELS[c]}</option>
            ))}
          </Form.Select>
          <Form.Control.Feedback type="invalid">
            {formik.touched.currency && formik.errors.currency}
          </Form.Control.Feedback>
        </Col>
      </Row>
    </div>
  );
};
